/**
 * COP Data Service
 * Optimized service layer for managing COP Dashboard API calls
 * Implements request cancellation, caching, debouncing, and prevents concurrent requests
 */

import { COPDeviceResponse } from './copApi';
import { authorizedFetch } from '@signals/app-shell/services/authorizedFetch';
import { geofenceService } from '@signals/app-shell/services/geofenceService';
import { ymdInTimeZoneToUtcInterval } from '@signals/app-shell/utils/time';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number;
}

interface TimelineActivityBucket {
  timestamp: string;
  total_count: number;
  student_count: number;
  rp_count: number;
  srp_count: number;
}

class COPDataService {
  private baseUrl: string = process.env.REACT_APP_API_URL || '/api';
  private cache = new Map<string, CacheEntry<any>>();
  private activeRequests = new Map<string, AbortController>();
  private pendingRequests = new Map<string, Promise<any>>();
  
  // Cache TTLs (in milliseconds)
  private readonly DEVICE_CACHE_TTL = 10000; // 10 seconds for device data
  private readonly TIMELINE_CACHE_TTL = 30000; // 30 seconds for timeline data  
  private readonly GEOFENCE_CACHE_TTL = 60000; // 1 minute for geofence data
  
  /**
   * Generate cache key from request params
   */
  private getCacheKey(endpoint: string, params?: any): string {
    return `${endpoint}:${JSON.stringify(params || {})}`;
  }
  
  /**
   * Check if cached data is still valid
   */
  private isCacheValid<T>(key: string): { valid: boolean; data?: T } {
    const cached = this.cache.get(key);
    if (!cached) return { valid: false };
    
    const now = Date.now();
    if (now > cached.timestamp + cached.expiresIn) {
      this.cache.delete(key);
      return { valid: false };
    }
    
    return { valid: true, data: cached.data };
  }
  
  /**
   * Cancel any active request for the given key
   */
  private cancelActiveRequest(key: string) {
    const controller = this.activeRequests.get(key);
    if (controller) {
      controller.abort();
      this.activeRequests.delete(key);
      this.pendingRequests.delete(key);
    }
  }
  
  /**
   * Make an API request with caching and cancellation support
   */
  private async makeRequest<T>(
    key: string, 
    _endpoint: string, 
    ttl: number,
    requestFn: (signal: AbortSignal) => Promise<T>
  ): Promise<T> {
    // Check cache first
    const cached = this.isCacheValid<T>(key);
    if (cached.valid && cached.data) {
      return cached.data;
    }
    
    // Check if there's already a pending request
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending;
    }
    
    // Cancel any previous request
    this.cancelActiveRequest(key);
    
    // Create new request
    const controller = new AbortController();
    this.activeRequests.set(key, controller);
    
    const requestPromise = (async () => {
      try {
        const data = await requestFn(controller.signal);
        
        // Cache the result
        this.cache.set(key, {
          data,
          timestamp: Date.now(),
          expiresIn: ttl
        });
        
        return data;
      } finally {
        this.activeRequests.delete(key);
        this.pendingRequests.delete(key);
      }
    })();
    
    this.pendingRequests.set(key, requestPromise);
    return requestPromise;
  }
  
  /**
   * Convert selectedDate dropdown value to days parameter for API
   */
  private selectedDateToDays(selectedDate: string | null): number | null {
    if (!selectedDate) return null;
    
    switch (selectedDate) {
      case 'Last Hour':
        return 0.04;  // ~1 hour
      case 'Last 8 Hours':
        return 0.33;  // ~8 hours
      case 'Last 12 Hours':
        return 0.5;   // ~12 hours
      case 'Last 24 Hours':
        return 1;     // 24 hours
      case 'Last 48 Hours':
        return 2;     // 48 hours
      case 'Last 72 Hours':
        return 3;     // 72 hours
      case 'Last Week':
        return 7;     // 7 days
      default:
        return null;  // No filter for custom dates or unknown values
    }
  }

  private appendSelectedDateFilter(
    queryParams: URLSearchParams,
    selectedDate: string | null,
    timeZone?: string,
    defaultDays?: string,
  ): void {
    if (!selectedDate) {
      if (defaultDays) queryParams.append('days', defaultDays);
      return;
    }

    switch (selectedDate) {
      case 'Last Hour':
        queryParams.append('days', '0.04');
        break;
      case 'Last 8 Hours':
        queryParams.append('days', '0.33');
        break;
      case 'Last 12 Hours':
        queryParams.append('days', '0.5');
        break;
      case 'Last 24 Hours':
        queryParams.append('days', '1');
        break;
      case 'Last 48 Hours':
        queryParams.append('days', '2');
        break;
      case 'Last 72 Hours':
        queryParams.append('days', '3');
        break;
      case 'Last Week':
        queryParams.append('days', '7');
        break;
      default:
        if (selectedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const utcBounds = timeZone ? ymdInTimeZoneToUtcInterval(selectedDate, timeZone) : null;
          if (utcBounds) {
            queryParams.append('start_date', utcBounds.startUtc);
            queryParams.append('end_date', utcBounds.endUtc);
          } else {
            queryParams.append('date', selectedDate);
          }
        } else if (defaultDays) {
          queryParams.append('days', defaultDays);
        }
        break;
    }
  }
  
  /**
   * Fetch device locations with caching and request management
   */
  async getDeviceLocations(activeOnly: boolean = false, selectedDate: string | null = null): Promise<COPDeviceResponse> {
    const days = this.selectedDateToDays(selectedDate);
    const params = { activeOnly, days };
    const cacheKey = this.getCacheKey('cop-devices', params);
    
    return this.makeRequest(
      cacheKey,
      'cop-devices',
      this.DEVICE_CACHE_TTL,
      async (signal) => {
        const queryParams = new URLSearchParams();
        if (activeOnly) {
          queryParams.append('active_only', activeOnly.toString());
        }
        if (days !== null) {
          queryParams.append('days', days.toString());
        }
        
        const url = `${this.baseUrl}/pathfinder-postgres/cop-devices${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        
        const response = await authorizedFetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          signal
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        return await response.json();
      }
    );
  }
  
  /**
   * Fetch timeline activity with caching and request management
   */
  async getTimelineActivity(params: {
    selectedDate: string | null;
    timeZone?: string;
    selectedCourse: string | null;
    selectedDeviceType: string | null;
    selectedEntityType?: string | null;
    selectedEntity?: string | null;
    bounds?: { north: number; south: number; east: number; west: number };
  }): Promise<TimelineActivityBucket[]> {
    const cacheKey = this.getCacheKey('timeline-activity', params);
    
    return this.makeRequest(
      cacheKey,
      'timeline-activity',
      this.TIMELINE_CACHE_TTL,
      async (signal) => {
        const queryParams = new URLSearchParams();
        
        this.appendSelectedDateFilter(queryParams, params.selectedDate, params.timeZone);
        
        if (params.selectedCourse && params.selectedCourse !== 'All Courses') {
          queryParams.append('course_id', params.selectedCourse);
        }
        if (params.selectedDeviceType && params.selectedDeviceType !== 'All') {
          queryParams.append('device_type', params.selectedDeviceType);
        }
        if (params.selectedEntityType && params.selectedEntityType !== 'All') {
          queryParams.append('entity_type', params.selectedEntityType);
        }
        if (params.selectedEntity && params.selectedEntity !== 'All') {
          queryParams.append('entity_id', params.selectedEntity);
        }
        // Add bounds filter for map extent
        if (params.bounds) {
          queryParams.append('bounds', `${params.bounds.west},${params.bounds.south},${params.bounds.east},${params.bounds.north}`);
        }
        queryParams.append('interval_minutes', '60');
        
        const url = `${this.baseUrl}/pathfinder-postgres/timeline-activity?${queryParams}`;
        
        const response = await authorizedFetch(url, { signal });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch timeline activity: ${response.status}`);
        }
        
        return await response.json();
      }
    );
  }
  
  /**
   * Fetch dwell timeline activity with caching and request management
   */
  async getTimelineDwells(params: {
    selectedDate: string | null;
    timeZone?: string;
    selectedCourse: string | null;
    selectedDeviceType: string | null;
    selectedEntityType?: string | null;
    selectedEntity?: string | null;
    bounds?: { north: number; south: number; east: number; west: number };
  }): Promise<TimelineActivityBucket[]> {
    const cacheKey = this.getCacheKey('timeline-dwells', params);
    
    return this.makeRequest(
      cacheKey,
      'timeline-dwells',
      this.TIMELINE_CACHE_TTL,
      async (signal) => {
        const queryParams = new URLSearchParams();
        
        // No explicit window - show the last week so sparse dwells still render.
        this.appendSelectedDateFilter(queryParams, params.selectedDate, params.timeZone, '7');
        
        if (params.selectedCourse && params.selectedCourse !== 'All Courses') {
          queryParams.append('course_id', params.selectedCourse);
        }
        if (params.selectedDeviceType && params.selectedDeviceType !== 'All') {
          queryParams.append('device_type', params.selectedDeviceType);
        }
        if (params.selectedEntityType && params.selectedEntityType !== 'All') {
          queryParams.append('entity_type', params.selectedEntityType);
        }
        if (params.selectedEntity && params.selectedEntity !== 'All') {
          queryParams.append('entity_id', params.selectedEntity);
        }
        // Add bounds filter for map extent
        if (params.bounds) {
          queryParams.append('bounds', `${params.bounds.west},${params.bounds.south},${params.bounds.east},${params.bounds.north}`);
        }
        queryParams.append('interval_minutes', '60');
        
        const url = `${this.baseUrl}/pathfinder-postgres/timeline-dwells?${queryParams}`;
        
        const response = await authorizedFetch(url, { signal });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch dwell timeline: ${response.status}`);
        }
        
        return await response.json();
      }
    );
  }
  
  /**
   * Fetch geofence timeline activity with caching and request management
   */
  async getTimelineGeofences(params: {
    selectedDate: string | null;
    timeZone?: string;
    selectedCourse: string | null;
    selectedDeviceType: string | null;
    selectedEntityType?: string | null;
    selectedEntity?: string | null;
    bounds?: { north: number; south: number; east: number; west: number };
  }): Promise<TimelineActivityBucket[]> {
    const cacheKey = this.getCacheKey('timeline-geofences', params);
    
    return this.makeRequest(
      cacheKey,
      'timeline-geofences',
      this.TIMELINE_CACHE_TTL,
      async (signal) => {
        const queryParams = new URLSearchParams();
        
        this.appendSelectedDateFilter(queryParams, params.selectedDate, params.timeZone, '7');
        
        if (params.selectedCourse && params.selectedCourse !== 'All Courses') {
          queryParams.append('course_id', params.selectedCourse);
        }
        if (params.selectedDeviceType && params.selectedDeviceType !== 'All') {
          queryParams.append('device_type', params.selectedDeviceType);
        }
        if (params.selectedEntityType && params.selectedEntityType !== 'All') {
          queryParams.append('entity_type', params.selectedEntityType);
        }
        if (params.selectedEntity && params.selectedEntity !== 'All') {
          queryParams.append('entity_id', params.selectedEntity);
        }
        // Add bounds filter for map extent
        if (params.bounds) {
          queryParams.append('bounds', `${params.bounds.west},${params.bounds.south},${params.bounds.east},${params.bounds.north}`);
        }
        queryParams.append('interval_minutes', '60');
        
        const url = `${this.baseUrl}/pathfinder-postgres/timeline-geofences?${queryParams}`;
        
        const response = await authorizedFetch(url, { signal });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch geofence timeline: ${response.status}`);
        }
        
        return await response.json();
      }
    );
  }
  
  /**
   * Fetch geofences with caching
   */
  async getGeofences(activeOnly: boolean = true): Promise<any[]> {
    const cacheKey = this.getCacheKey('geofences', { activeOnly });
    
    return this.makeRequest(
      cacheKey,
      'geofences',
      this.GEOFENCE_CACHE_TTL,
      async (_signal) => {
        // Use existing geofence service but add abort signal support
        // Pass undefined for courseId and activeOnly as second parameter
        const result = await geofenceService.getGeofences(undefined, activeOnly);
        return result;
      }
    );
  }
  
  /**
   * Clear all caches
   */
  clearCache() {
    this.cache.clear();
  }
  
  /**
   * Clear specific cache entry
   */
  clearCacheEntry(endpoint: string, params?: any) {
    const key = this.getCacheKey(endpoint, params);
    this.cache.delete(key);
  }
  
  /**
   * Cancel all active requests
   */
  cancelAllRequests() {
    this.activeRequests.forEach((controller) => {
      controller.abort();
    });
    this.activeRequests.clear();
    this.pendingRequests.clear();
  }
}

export const copDataService = new COPDataService();
